// Обращения оболочки: свежесть, кто вошёл, меню.
//
// ⚠⚠ **Шапка не ходит на `/api/s-10/state`.** Свежесть — признак данных, а не
// поверхность: у человека, которому `S-10` не выдана, она обязана оставаться
// (решение оркестратора 84; `carry.md`, строки таска 27 про свежесть в `З`,
// `ПМ`, `НВ`). Поэтому у шапки свои источники, открытые каждой действующей
// учётной записи:
//
// - `GET /api/auth/freshness` — случай индикатора, без журнала прогонов и без
//   отметки пресета: журнал до экрана не доходит вовсе;
// - `GET /api/auth/me` — логин и выданные поверхности (профиль в шапке, переход
//   индикатора на `S-10` только тем, кому она выдана);
// - `GET /api/shell/menu` — меню из `CFG-141`, невыданное сервер уже убрал;
// - `GET /api/shell/attention` и `PUT /api/shell/attention/seen` — «Потребує уваги»
//   (дела из текущих чисел, у человека хранится только «видел»; F-013, решение 066);
// - `GET /api/shell/status` — резервная копия правой половины строки состояния.
//
// ⚠ Слов здесь нет: индикатор приходит кодом случая, слово — словарь.

import { z } from "zod";

import { Attention, AttentionSeen, HeaderFreshness, Me, ShellStatus, type AttentionItem, type BackupLastRow } from "@/api/schemas";
import { say, wordOf, wordsFor } from "@/api/words";

/**
 * Свежесть так, как её печатает шапка: слово индикатора на месте кода. Код
 * остаётся рядом (`code`) — по нему выбирается тон значка, а не слово.
 */
export type Shown = Omit<HeaderFreshness, "indicator"> & {
  readonly indicator: string;
  readonly code?: HeaderFreshness["indicator"];
};

/** Чем кончилось обращение за свежестью. */
export type FreshAnswer =
  | { readonly kind: "state"; readonly state: Shown }
  /** Входа нет: шапке спрашивать не у кого, индикатор молчит. */
  | { readonly kind: "no-session" }
  /** `ПМ`: не ответила наша страница, а не DnTrade. Слова — словарь по коду. */
  | { readonly kind: "cannot-show"; readonly text_uk: string }
  /** Система не ответила вовсе либо ответила нечитаемым. */
  | { readonly kind: "silent" };

/** Пункт меню так, как его отдаёт сервер (`api/shell.py`, `menu_for`). */
export const MenuItem = z.strictObject({
  code: z.string(),
  label_uk: z.string(),
  mark: z.enum(["РЕД", "ТЧ"]),
});
export type MenuItem = z.infer<typeof MenuItem>;

export const Menu = z.strictObject({
  /** Верхняя строка над группами: «Дашборд», «Сповіщення». */
  top: z.array(MenuItem),
  groups: z.array(z.strictObject({
    group: z.string(),
    name_uk: z.string().nullable(),
    items: z.array(MenuItem),
  })),
});
export type Menu = z.infer<typeof Menu>;

async function json(path: string): Promise<{ status: number; body: unknown } | null> {
  try {
    const answer = await fetch(path, { headers: { accept: "application/json" } });
    let body: unknown = null;
    try {
      body = await answer.json();
    } catch {
      body = null;
    }
    return { status: answer.status, body };
  } catch {
    return null;
  }
}

export async function askFreshness(): Promise<FreshAnswer> {
  const got = await json("/api/auth/freshness");
  if (got === null) return { kind: "silent" };
  if (got.status === 401) return { kind: "no-session" };
  const kind = (got.body as { kind?: unknown } | null)?.kind;
  if (got.status === 503 && typeof kind === "string") {
    return { kind: "cannot-show", text_uk: await wordOf(kind) };
  }
  if (got.status !== 200) return { kind: "silent" };
  const parsed = HeaderFreshness.safeParse(got.body);
  if (!parsed.success) {
    console.error(parsed.error);
    return { kind: "silent" };
  }
  const code = parsed.data.indicator;
  return { kind: "state", state: { ...parsed.data, code, indicator: say(await wordsFor([code]), code) } };
}

/** Кто вошёл. Входа нет или система молчит — `null`. */
export async function askMe(): Promise<Me | null> {
  const got = await json("/api/auth/me");
  if (got === null || got.status !== 200) return null;
  const parsed = Me.safeParse(got.body);
  if (!parsed.success) {
    console.error(parsed.error);
    return null;
  }
  return parsed.data;
}

/**
 * Настройка меню испорчена (`menu_setting_broken`, правило 10): сервер отказал с
 * причиной, слово причины — словарь. Меню не рисуется, «Дашборд» доступен через логотип.
 */
export interface MenuBroken {
  readonly broken: string;
}

/** Меню по правам входа. Не ответило — `null`, и меню не рисуется вовсе. */
export async function askMenu(): Promise<Menu | MenuBroken | null> {
  const got = await json("/api/shell/menu");
  if (got === null) return null;
  const kind = (got.body as { kind?: unknown } | null)?.kind;
  if (got.status === 503 && kind === "menu_setting_broken") return { broken: await wordOf(kind) };
  if (got.status !== 200) return null;
  const parsed = Menu.safeParse(got.body);
  if (!parsed.success) {
    console.error(parsed.error);
    return null;
  }
  return parsed.data;
}

const Favorites = z.strictObject({ codes: z.array(z.string()) });

/** «Обране» человека: коды пунктов его меню. Не ответило — `null`. */
export async function askFavorites(): Promise<string[] | null> {
  const got = await json("/api/shell/favorites");
  if (got === null || got.status !== 200) return null;
  const parsed = Favorites.safeParse(got.body);
  return parsed.success ? parsed.data.codes : null;
}

/** Сохранить «Обране». Отказ или молчание сервера — `null`: тихо не теряется. */
export async function putFavorites(codes: readonly string[]): Promise<string[] | null> {
  try {
    const answer = await fetch("/api/shell/favorites", {
      method: "PUT",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ codes }),
    });
    if (answer.status !== 200) return null;
    const parsed = Favorites.safeParse(await answer.json());
    return parsed.success ? parsed.data.codes : null;
  } catch {
    return null;
  }
}

/** Дела «Потребує уваги». Не ответило — `silent`: это «не знаю», а не «дел нет». */
export type AttentionAnswer =
  | { readonly kind: "items"; readonly items: readonly AttentionItem[] }
  | { readonly kind: "silent" };

export async function askAttention(): Promise<AttentionAnswer> {
  const got = await json("/api/shell/attention");
  if (got === null || got.status !== 200) return { kind: "silent" };
  const parsed = Attention.safeParse(got.body);
  if (!parsed.success) {
    console.error(parsed.error);
    return { kind: "silent" };
  }
  return { kind: "items", items: parsed.data.items };
}

/** Отметить дела виденными. Не записалось — `false`: дело останется «новым» и раскроет плашку снова. */
export async function putSeen(keys: readonly string[]): Promise<boolean> {
  try {
    const answer = await fetch("/api/shell/attention/seen", {
      method: "PUT",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ keys }),
      // Уход со страницы (`pagehide`) не должен отменять запись «видел».
      keepalive: true,
    });
    return answer.status === 200 && AttentionSeen.safeParse(await answer.json()).success;
  } catch {
    return false;
  }
}

/** Резервная копия для строки состояния. `hidden` — `S-10` человеку закрыта: строки нет вовсе. */
export type BackupAnswer =
  | { readonly kind: "hidden" }
  | { readonly kind: "row"; readonly row: BackupLastRow }
  | { readonly kind: "silent" };

export async function askStatus(): Promise<BackupAnswer> {
  const got = await json("/api/shell/status");
  if (got === null || got.status !== 200) return { kind: "silent" };
  const parsed = ShellStatus.safeParse(got.body);
  if (!parsed.success) {
    console.error(parsed.error);
    return { kind: "silent" };
  }
  return parsed.data.backup === null ? { kind: "hidden" } : { kind: "row", row: parsed.data.backup };
}
